import type {
  Block, Cell, Column, DecideBlock, DecideLine, DecidePanel, DecideReason, NoteBlock,
  TableBlock, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { holdsPermission, parseSession, type StaffSession } from '@/lib/auth/session'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { REASON_MAX_LENGTH } from './reasoncode'

/**
 * Handles — the reserved word list, and the queue where a person decides whether
 * a particular human may be a particular name (#111, design brief §6.18).
 *
 * # Nothing here moves money, and none of the vocabulary that does may be reused
 *
 * No `decision_ref` is minted by any of these routes. A handle authorises
 * nothing, this is not an `authz.Authorize` path, and an item from this queue
 * must never appear in the decision explorer. Law 14's four-outcome chip set
 * (`allow` / `review` / `step_up` / `refuse_to_submit`) is a DIFFERENT closed
 * vocabulary and is deliberately absent from this file. There are three closed
 * sets on this surface and they are three:
 *
 *   - a reserved term's `action`  — `refuse` | `review`
 *   - a review's decided `state`  — `allowed` | `refused`
 *   - a claim's `outcome`         — `claimed` | `held_for_review` | `refused`
 *
 * Law 13's "every refusal surfaces a `decision_ref`" is answered here by the
 * claim journal instead: the reference handed to a support agent is the claim
 * `id` plus `policy_id` and `policy_version` (see `handleclaims.ts`). Nothing in
 * this file invents a decision reference to fill that slot.
 *
 * # The one write in this console
 *
 * `POST /handles/reviews/{id}/decision` is the first mutation this console has
 * ever performed. It is gated on the PERMISSION `handle.impersonation.decide`
 * and never on a role: the backend grants that key to `compliance` alone, and
 * `admin` implies nothing in `staff.implies`, so role-based gating would draw
 * the control for an Admin the server refuses and hide it from a Compliance
 * officer on any screen whose role list forgot to name them. The closure the
 * server reports on `/v1/admin/me` is the only correct source (go-wallet#688),
 * which is why both loaders here read it alongside their own endpoint.
 *
 * # What is deliberately NOT built
 *
 * `POST /v1/admin/handles/reserved-terms` — publishing a reserved term — gets NO
 * control in this change. No button, no form, no disabled placeholder. It is
 * Phase 2 in the design brief's §7 table for one stated reason: the composer
 * cannot be made safe until a fold-preview route exists, and none does. There
 * is no dry-run, no fold endpoint and no match simulator anywhere on this
 * surface, so an operator would be publishing a rule whose actual reach — the
 * confusable fold — is computed server-side and shown only AFTER the word is
 * reserved. **This omission is a decision, not an oversight. Do not finish it.**
 * A console-side approximation of the fold is worse than none, because the
 * operator would trust it.
 *
 * Also absent, permanently, because no route exists: reassigning a held handle
 * to another customer, releasing a retired name back to the pool, editing
 * `opened_reason`, bulk-deciding, snoozing, deciding on behalf of another
 * operator, and any edit or delete of a reserved term (there is no PUT and no
 * DELETE, by database trigger).
 */

// ---------------------------------------------------------------------------
// The endpoints
// ---------------------------------------------------------------------------

export const RESERVED_TERMS_PATH = '/handles/reserved-terms'
export const REVIEW_QUEUE_PATH = '/handles/reviews'

/** The decision on one review. The client adds the `/v1/admin` prefix. */
export function reviewDecisionPath(reviewID: number): string {
  return `${REVIEW_QUEUE_PATH}/${String(reviewID)}/decision`
}

/** The caller's own session, read for its permission closure and its cell. */
export const ME_PATH = '/me'

const TERMS_SOURCE = 'postgres · handle_reserved_terms (GET /v1/admin/handles/reserved-terms)'
const QUEUE_SOURCE = 'postgres · handle_impersonation_reviews (GET /v1/admin/handles/reviews)'

/**
 * Our own record and answerable to nobody else.
 *
 * A handle is not an institution-side object: FinXP has never heard of it, there
 * is no provider figure to lose a discrepancy to (invariant 2), and no money is
 * denominated in it. `operational` is the whole truth rather than one side of a
 * comparison.
 */
const AUTHORITY = 'operational'

/**
 * The permission key the decision declares as `x-staff-permission` in
 * `api/openapi-admin.yaml`.
 *
 * The reads declare `handle.read` and `customer.read` and are NOT named here on
 * purpose: this console never gates a read on a permission of its own. The
 * server refuses one it should not serve, and a read the server answered is a
 * read the operator may see (Part I §5) — pre-empting it would hide a screen the
 * backend was willing to give. Only the CONTROL is gated, and only against the
 * key the server enforces for it.
 */
export const PERM_HANDLE_DECIDE = 'handle.impersonation.decide'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so an id past 2^53 has
 * already lost digits by the time it reaches here. A review id wrong in its last
 * places is one an operator would decide — against a different person's name.
 */
function exactInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

/** An RFC-3339 instant in the format the design draws timestamps in. */
function stamp(iso: string): string {
  if (iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

// ===========================================================================
// §6.18.2 — RESERVED TERMS, READ ONLY
// ===========================================================================

/** One reserved term, as `handleReservedTermView` serialises it. All required. */
export interface ReservedTermView {
  readonly id: number
  readonly version: number
  readonly term: string
  readonly skeleton: string
  /** `exact | token | substring`. Read fail-closed. */
  readonly match: string
  /** `refuse | review`. Read fail-closed. */
  readonly action: string
  /** One of seven categories. Read fail-closed. */
  readonly category: string
  readonly reason: string
}

export interface ReservedTermsResponse {
  /** `unknown[]`: a loop dereferences it, so every row is narrowed explicitly. */
  readonly terms: readonly unknown[]
}

interface TermRow {
  readonly id: number | null
  readonly version: number | null
  readonly term: string
  readonly skeleton: string
  readonly match: string
  readonly action: string
  readonly category: string
  readonly reason: string
}

function readTerm(raw: unknown): TermRow | null {
  // `typeof [] === 'object'`, so an array has to be refused explicitly: it would
  // otherwise narrow to a row with every field absent and be COUNTED as read —
  // a ghost row of dashes on the list that says which words are kept back.
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const t = raw as Partial<ReservedTermView>
  return {
    id: exactInteger(t.id),
    version: exactInteger(t.version),
    term: text(t.term),
    skeleton: text(t.skeleton),
    match: text(t.match),
    action: text(t.action),
    category: text(t.category),
    reason: text(t.reason),
  }
}

/**
 * What each match mode actually reaches, in words.
 *
 * `substring` is the dangerous one and its label says so inline rather than in a
 * tooltip: a short substring term makes unrelated names unclaimable with no
 * error anybody can read — `id` in substring mode would refuse `@davidson`,
 * `@madrid` and `@kidsclub`.
 *
 * Exported, with the three maps below it, for one reason: the tests that bind
 * each of these SENTENCES to the wire value it renders are driven off the maps
 * themselves, so a mode added to the closed set later cannot arrive with nothing
 * asserting what its chip promises. Swapping two entries of a map like this is
 * invisible to a test that only counts rows or checks a value the map never
 * touches, and an operator reading "exact · the whole name and nothing else" on a
 * substring term cannot predict what they have reserved.
 */
export const MATCH_LABEL: Readonly<Record<string, string>> = {
  exact: 'exact · the whole name and nothing else',
  token: 'token · any hyphen-delimited word',
  substring: 'substring · anywhere in the fold',
}

export const MATCH_TONE: Readonly<Record<string, Tone>> = {
  exact: 'n',
  token: 'n',
  substring: 'a',
}

/**
 * `refuse` and `review` are not degrees of the same thing.
 *
 * `refuse` is mechanical and fail-closed and costs nobody anything. `review`
 * HOLDS the claim for a person: the name is taken against everybody else and
 * resolves to nobody meanwhile, so publishing a `review` term is publishing work
 * into a Compliance queue and a creator's pay-me link into the dark. The chip
 * says that rather than presenting `review` as the softer option.
 */
export const ACTION_LABEL: Readonly<Record<string, string>> = {
  refuse: 'refuse · mechanical',
  review: 'review · holds the name for a person',
}

export const ACTION_TONE: Readonly<Record<string, Tone>> = {
  refuse: 'n',
  review: 'a',
}

const CATEGORIES: readonly string[] = [
  'brand', 'operator', 'product', 'route', 'regulatory', 'offensive', 'high_profile',
]

/** A closed-set cell, shown verbatim and toned `r` when it is outside the set. */
function closedChip(
  value: string, labels: Readonly<Record<string, string>>, tones: Readonly<Record<string, Tone>>,
): Cell {
  if (value === '') return ['not returned', 'r']
  if (!Object.prototype.hasOwnProperty.call(tones, value)) {
    return [`${value} · unknown to this console`, 'r']
  }
  return [labels[value] ?? value, tones[value] ?? 'r']
}

const TERM_COLS: readonly Column[] = [
  ['id', 'm', '72px'],
  ['v', 'n', '48px'],
  ['Term as published', 'x2', 'minmax(140px,1fr)'],
  // The fold is a PRIMARY column and never a detail behind a reveal. `admin` and
  // `admln` in the same row is the entire argument for showing it: an operator
  // who cannot see the fold cannot predict what they have just reserved.
  ['Fold actually matched', 'm', 'minmax(140px,1fr)'],
  ['Match', 'c', '230px'],
  ['Action', 'c', '230px'],
  ['Category', 'x', '124px'],
  ['Why it is kept back', 'x', 'minmax(220px,1.6fr)'],
]

function termRow(t: TermRow): readonly Cell[] {
  return [
    t.id === null ? '— no id' : String(t.id),
    t.version === null ? '—' : String(t.version),
    t.term === '' ? '— not returned' : t.term,
    // Never blank. A blank fold column reads as "this term matches itself only",
    // which is the one reading that makes a substring term look harmless.
    t.skeleton === '' ? '— not returned' : t.skeleton,
    closedChip(t.match, MATCH_LABEL, MATCH_TONE),
    closedChip(t.action, ACTION_LABEL, ACTION_TONE),
    t.category === ''
      ? '— not returned'
      : CATEGORIES.includes(t.category)
        ? t.category
        : `${t.category} · unknown`,
    t.reason === '' ? '— no reason recorded' : t.reason,
  ]
}

/**
 * The absences this screen must state rather than design around (§0, law 6).
 *
 * Law 6 requires a versioned entity's editor to be labelled "Save as new
 * version", to show a diff against live, and to keep prior versions visible and
 * marked immutable. Only the first is satisfiable here and there is no editor to
 * put it on. The read returns the list in force AT THE INSTANT OF THE REQUEST
 * and nothing else: no superseded rows, no `at` parameter, and `published_by`,
 * `effective_from`, `effective_to` and `created_at` are on the table and not on
 * the wire. So the history and diff panes are present, honestly empty, and
 * labelled with what would fill them — rather than drawn out of data the console
 * does not have.
 */
const TERMS_HISTORY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'History · diff',
  title: 'This list is the rules in force right now, and this screen cannot show any other instant',
  body: 'Law 6 asks a versioned entity to show a diff against live and to keep prior versions visible and immutable. Neither is satisfiable against this endpoint: it returns only the rows governing this instant, it takes no "as at" parameter, and published_by, effective_from, effective_to and created_at exist on the table but are not on the wire. So there is no author column, no published-at column and no version-history drawer here — not because they were forgotten, but because there is no data behind any of them. "Who reserved this word, and when" is answerable today only from the audit log, action admin.handle.reserved_term.publish.',
  ref: 'The panes are empty on purpose · a diff drawn from data we do not have would be worse than none',
}

/**
 * Why there is no publish control, said out loud so nobody finishes it.
 *
 * The design brief's §4 permission-UX rule would ordinarily want the control
 * drawn and disabled naming Ops. It is not drawn at all here, and the two are
 * reconciled by which failure each prevents: a disabled button implies a wired
 * route an operator can be escalated into, and there is no safe route to
 * escalate into. #111 makes "no control anywhere that publishes one" an
 * acceptance criterion for exactly that reason. A stated absence is the honest
 * rendering of a control that must not exist yet; a greyed button is not.
 */
const NO_PUBLISH_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'What this screen can do',
  title: 'Read only — and there is deliberately no control that publishes a term',
  body: 'Publishing a reserved term is Phase 2 and is absent by decision, not by oversight: it cannot be made safe until a fold-preview route exists, and none does. There is no dry-run, no fold endpoint and no match simulator anywhere on this surface, so the confusable fold a term will actually match on is computed server-side and shown only after the word is reserved. An operator would be publishing a rule whose reach they cannot see. Reimplementing the fold in this console is worse than not offering the control, because a client-side approximation that disagrees with the server by one substitution is one an operator would trust. There is also no edit and no delete affordance on a row, and there never will be: there is no PUT and no DELETE on this resource, enforced by a database trigger — un-reserving a word means publishing a version that reserves nothing, and this console cannot express that either.',
  ref: 'design brief §6.18.2 and §7 · go-wallet-admin-client#111 · do not "finish" this',
}

function termsTable(rows: readonly TermRow[], unreadable: number): TableBlock {
  const head = `${String(rows.length)} term(s) in force at the instant of this read`
  return {
    t: 'table',
    title: 'Reserved handle terms',
    src: 'ledger',
    // No filter was applied and the endpoint takes none, so no facet bar rather
    // than an empty one implying a filter exists.
    facets: false,
    cols: TERM_COLS,
    rows: rows.map(termRow),
    footer: unreadable === 0
      ? head
      : `${head} · ${String(unreadable)} row(s) arrived and could not be read`,
    foot2:
      'Ordered by id. There are no filters, no paging, no limit and no cursor on this endpoint, and no envelope — so there is no "showing N of M" to draw. The fold column is the one that matters: publishing "support" also refuses "supp0rt", "s-u-p-p-o-r-t" and "5upport".',
  }
}

/**
 * An empty reserved list is an ALARM, not a shrug.
 *
 * The table ships seeded with fifteen rows, so empty is effectively unreachable
 * — and that is what makes it worth stating loudly rather than quietly. An empty
 * reserved list means every operator word is claimable: `@support`, `@billing`,
 * `@security` and `@refund` are all available to whoever asks first.
 */
const TERMS_EMPTY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'r',
  kicker: 'Reserved terms',
  title: 'The reserved list is empty, and that is not a quiet state',
  body: 'The backend answered and returned no reserved term at all. This table ships seeded with fifteen rows, so an empty list is not the ordinary case: it means no word is kept back and every operator name is claimable right now — @support, @billing, @security, @refund and @admin among them. Treat this as an incident and check whether the seed ran, rather than as a list nobody has filled in yet.',
  ref: 'An empty blocklist refuses nothing.',
}

/**
 * Folds the reserved-terms response into the screen.
 *
 * Pure and separate from the fetch, so every absence path is table-testable
 * without a client. `readAt` is a parameter rather than an ambient `new Date()`
 * — this endpoint sends no `generated_at`, so the honest stamp is the moment the
 * console read it, and it has to be supplied rather than taken.
 */
export function composeReservedTerms(res: ReservedTermsResponse, readAt: Date): ScreenData {
  const rows: TermRow[] = []
  let unreadable = 0
  for (const raw of res.terms) {
    const parsed = readTerm(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [termsTable(rows, unreadable)]
  if (rows.length === 0 && unreadable === 0) blocks.push(TERMS_EMPTY_NOTE)
  blocks.push(TERMS_HISTORY_NOTE)
  blocks.push(NO_PUBLISH_NOTE)

  const missing = [
    'who published each term and when (published_by, effective_from, effective_to are not on the wire)',
    'superseded versions, and therefore any diff against live',
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)

  return {
    blocks,
    provenance: {
      kind: 'partial',
      source: TERMS_SOURCE,
      authority: AUTHORITY,
      readAt: readAtStamp(readAt),
      missing,
    },
  }
}

/**
 * The screen when the list could not be read at all.
 *
 * **No cached fallback and no corpus fallback**, and the reason is specific to
 * this screen: a stale reserved list read as current is how an operator answers
 * "why can I not have that name" with a rule that no longer exists — or worse,
 * fails to warn about one that does. The design corpus's fifteen invented terms
 * would be exactly that, with the console's badge saying live beside them.
 */
function termsUnread(reason: string): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Reserved terms',
      title: 'The reserved list could not be read',
      body: `${reason} No term below is known, and nothing is shown from a cache or from the design corpus: a stale reserved list read as current is how an operator publishes a term that already exists, or tells a customer a word is free when it is kept back. This is not an empty list.`,
      ref: 'An unread blocklist is not an empty one.',
    }],
    provenance: { kind: 'unread', source: TERMS_SOURCE, reason: reason },
  }
}

function isReservedTermsResponse(payload: unknown): payload is ReservedTermsResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<ReservedTermsResponse>).terms)
}

/** Why a read did not answer, in one sentence quoting what the backend said. */
function whyUnread(subject: string, error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.code === null ? '' : ` (${error.code})`
    const answered = `${subject} answered ${String(error.status)}${code}.`
    if (error.status === 401) return `${answered} The session is not authenticated.`
    if (error.status === 403) {
      // Quote the refusal; never restate the permission model. The backend sends
      // `required_role` on an RBAC denial precisely so the console does not
      // carry a second copy of a model it cannot keep in step.
      const named = text(error.requiredRole)
      return named === ''
        ? `${answered} It named no required role, so what this session is missing is not guessed at here.`
        : `${answered} It states the required role as ${JSON.stringify(named)}.`
    }
    return answered
  }
  return `${subject} could not be reached.`
}

export const loadReservedTerms: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(RESERVED_TERMS_PATH)
  } catch (error) {
    // A navigation away is a cancellation, not a finding about the list.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return termsUnread(whyUnread('The reserved-terms endpoint', error))
  }
  if (!isReservedTermsResponse(payload)) {
    return termsUnread('The reserved-terms endpoint returned a shape this console does not know: no terms array was on it.')
  }
  return composeReservedTerms(payload, new Date())
}

// ===========================================================================
// §6.18.1 — THE IMPERSONATION REVIEW QUEUE
// ===========================================================================

/** One queue row. Every field is required on the wire; all are read defensively. */
export interface ImpersonationReviewView {
  readonly id: number
  readonly handle: string
  readonly customer_id: number
  readonly claim_id: number
  readonly term_ids: readonly number[]
  readonly opened_at: string
  readonly opened_reason: string
}

export interface ReviewQueueResponse {
  readonly reviews: readonly unknown[]
}

export interface ReviewRow {
  readonly id: number | null
  readonly handle: string
  readonly customerId: number | null
  readonly claimId: number | null
  /** Exactly the ids that arrived as exact integers. */
  readonly termIds: readonly number[]
  /** How many entries in `term_ids` were not readable as ids. */
  readonly termIdsUnreadable: number
  readonly openedAt: string
  readonly openedReason: string
}

function readReview(raw: unknown): ReviewRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const r = raw as Partial<ImpersonationReviewView>
  const rawTerms: readonly unknown[] = Array.isArray(r.term_ids) ? r.term_ids : []
  const termIds: number[] = []
  let termIdsUnreadable = 0
  for (const entry of rawTerms) {
    const id = exactInteger(entry)
    if (id === null) termIdsUnreadable += 1
    else termIds.push(id)
  }
  return {
    id: exactInteger(r.id),
    handle: text(r.handle),
    customerId: exactInteger(r.customer_id),
    claimId: exactInteger(r.claim_id),
    termIds,
    termIdsUnreadable,
    openedAt: text(r.opened_at),
    openedReason: text(r.opened_reason),
  }
}

const QUEUE_COLS: readonly Column[] = [
  ['Review id', 'm', '92px'],
  // Rendered as `@name` and NEVER as a link. It resolves to nobody: the name is
  // held, taken against everybody else, and the creator's pay-me link is dark.
  ['Handle held', 'x2', 'minmax(150px,1.2fr)'],
  // The only customer datum on the row. No display name, no acceptance state —
  // the endpoint sends an id and nothing else, and a name invented beside it
  // would be the console attributing a judgement to the wrong person.
  ['Customer id', 'm', '104px'],
  ['Claim id', 'm', '92px'],
  ['Reviewing terms', 'x', '150px'],
  ['Opened', 'm', '150px'],
  ['Why it was held', 'x', 'minmax(240px,2fr)'],
]

/**
 * The reviewing-terms cell.
 *
 * A COUNT, not a resolution, and that is the point. `term_ids` does not reliably
 * resolve against the reserved-terms read: that read returns only the rows live
 * NOW, and a term since closed and superseded is absent from it — so a naive
 * join renders blanks on exactly the rows that most need explaining. The
 * resolvable source is the claim's `rule_snapshot`, which snapshots every term
 * consulted by id and version, and which is one row-open away.
 */
function termsCell(r: ReviewRow): string {
  const shown = r.termIds.length === 0 ? '— none named' : r.termIds.join(', ')
  const more = r.termIds.length > 1 ? ` (${String(r.termIds.length)})` : ''
  return r.termIdsUnreadable === 0
    ? `${shown}${more}`
    : `${shown}${more} · ${String(r.termIdsUnreadable)} unreadable`
}

/**
 * `opened_reason` is not the complete rationale, and the cell says so.
 *
 * The server composes one sentence naming only the FIRST matching term. Several
 * terms can match; where more than one did, presenting that sentence as the
 * whole answer is the console overstating what it was told.
 */
function reasonCell(r: ReviewRow): string {
  const sentence = r.openedReason === '' ? '— no reason recorded' : r.openedReason
  return r.termIds.length > 1
    ? `${sentence} — and ${String(r.termIds.length - 1)} further term(s) also matched; this sentence names only the first`
    : sentence
}

function queueRow(r: ReviewRow): readonly Cell[] {
  return [
    r.id === null ? '— no id' : String(r.id),
    r.handle === '' ? '— not returned' : `@${r.handle}`,
    r.customerId === null ? '—' : String(r.customerId),
    r.claimId === null ? '— not linked' : String(r.claimId),
    termsCell(r),
    // Never blank. On a queue sorted oldest-first the opened time is the only
    // ageing signal the row carries, and a blank reads as "just now".
    stamp(r.openedAt) === '' ? '— not returned' : stamp(r.openedAt),
    reasonCell(r),
  ]
}

function queueTable(rows: readonly ReviewRow[], unreadable: number): TableBlock {
  const head = `${String(rows.length)} handle(s) held for review, oldest first`
  return {
    t: 'table',
    title: 'Handles held for impersonation review',
    src: 'ledger',
    facets: false,
    cols: QUEUE_COLS,
    rows: rows.map(queueRow),
    footer: unreadable === 0
      ? head
      : `${head} · ${String(unreadable)} row(s) arrived and could not be read`,
    // No "showing 50 of N" anywhere, because there is no N. The endpoint returns
    // rows and nothing else: no total, no cursor, no offset and no filter.
    foot2:
      'This endpoint sends no total, no cursor and no filter, so there is no queue depth to state and a queue deeper than 200 is unreachable from here. Every name on this list is held: taken against everybody else and resolving to nobody, so the creator waiting on it has a pay-me link that does not work yet. Select a row to read the claim that produced the hold and to decide it.',
  }
}

/**
 * The empty queue — the GOOD state, and it must not read as a failed load.
 *
 * This is law 20 at its sharpest, in both directions. An operator who reads "no
 * reviews" as "the queue is broken" goes hunting a phantom; one who reads a
 * broken queue as "no reviews" leaves creators waiting invisibly with their
 * pay-me links dark. So an empty page and an unread page produce two different
 * screens with two different tones and two different sentences, and neither is
 * ever rendered for the other.
 *
 * It also says what the queue is FOR, per §9: near-empty is the construction of
 * this queue today rather than a staging artifact, because exactly one seeded
 * term carries `action: review`.
 */
const QUEUE_EMPTY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'g',
  kicker: 'Queue',
  title: 'No handles are held for review',
  body: 'Nobody is waiting on a person. A claim is held here only when its confusable fold matches a reserved term published with action: review — today that is "official", the one seeded term of fifteen that carries it. So a near-empty queue is what this control is expected to look like, not a screen that failed to load: the backend answered, and its answer is that nothing is held. Anything held here would be a creator whose pay-me link resolves to nobody while they wait.',
  ref: 'This is an answered zero, not an unread page.',
}

function unreadableRowsNote(unreadable: number): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Not shown',
    title: `${String(unreadable)} row(s) on this queue could not be read`,
    body: 'The backend sent entries this console could not read as reviews. They are counted rather than dropped, because a held handle missing from this queue is a creator nobody decides for — and the table above is shorter than the queue by exactly this many.',
    ref: 'An unread row is not an absent review.',
  }
}

/**
 * The screen when the queue itself could not be read.
 *
 * No cached list, ever. A stale queue is a person left waiting invisibly, and a
 * fabricated empty one is worse: "nobody is held" is the single most
 * consequential sentence this screen can say.
 */
function queueUnread(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Impersonation review queue',
      title: 'This queue could not be read',
      body: `${reason} No held handle is known — that is different from the queue being empty, and the difference matters more here than almost anywhere: an empty queue means nobody is waiting, and an unread one means nobody can tell. Nothing is shown from a cache or from the design corpus.`,
      ref: decisionRef === null
        ? 'This is not an empty queue.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: {
      kind: 'unread',
      source: QUEUE_SOURCE,
      reason,
    },
  }
}

// ---------------------------------------------------------------------------
// Who is reading, and what they may do about it
// ---------------------------------------------------------------------------

/**
 * The caller's own authority, as the backend reports it.
 *
 * Read alongside the queue rather than taken from the console's session state,
 * for one reason that outweighs the extra request: the adapter composes the
 * control, so the adapter needs the answer, and threading a session through
 * `ScreenLoader` would change the signature of forty-five loaders to serve one.
 *
 * Every failure resolves to "not held". A `/me` that refused, failed or was
 * never deployed tells us nothing about what this operator may do, and the
 * honest rendering of not knowing is the disabled control with the reason said
 * out loud — never the enabled one.
 */
export interface Caller {
  readonly session: StaffSession | null
  /** Why the session is unknown, when it is. `''` when it was read. */
  readonly reason: string
}

export async function readCaller(api: ApiClient): Promise<Caller> {
  try {
    const session = parseSession(await api.get<unknown>(ME_PATH))
    return session === null
      ? { session: null, reason: 'The session endpoint returned a shape this console does not know.' }
      : { session, reason: '' }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return { session: null, reason: whyUnread('The session endpoint', error) }
  }
}

/**
 * Why this operator may not decide, or `''` when they may.
 *
 * Three refusals, in the order they override each other. The standby cell comes
 * first because no permission changes it: a write on a standby cell is answered
 * `409 standby_cell` whatever the console drew (law 19).
 *
 * The permission refusal names **the Compliance role** in those words, because
 * that is what an operator has to ask for — `handle.impersonation.decide` is
 * granted to `compliance` and to nothing else, and telling a Support agent the
 * name of a permission key sends them to look up something they cannot request.
 * Both are named, in that order, so the escalation path is legible: law 18 says
 * a gated control is shown disabled with the required role named, never hidden,
 * precisely so staff know the path exists.
 */
export function decideRefusal(caller: Caller): string {
  if (caller.session?.cell?.role === 'standby') {
    return 'Disabled — this is the standby cell. The decision happens on the primary, and this backend answers 409 standby_cell whatever is drawn here.'
  }
  if (caller.session === null) {
    return `Disabled — this console could not read what your session may do, so it is treating the permission as not held. ${caller.reason} A control drawn on an unread permission is one that promises a judgement the server refuses.`
  }
  if (!holdsPermission(caller.session, PERM_HANDLE_DECIDE)) {
    return 'Disabled — Requires the Compliance role. Deciding a held handle needs the permission handle.impersonation.decide, which is granted to Compliance and to nobody else; reading this queue needs only handle.read, which Support and Ops hold. The queue above is fully readable and not one row of it is actionable from this session.'
  }
  return ''
}

// ---------------------------------------------------------------------------
// The queue screen
// ---------------------------------------------------------------------------

/**
 * The identity a queue row opens into.
 *
 * Three ids joined, because opening a review needs all three and none of them
 * can be recovered from the others: the review is what gets decided, the claim
 * is where the rationale actually lives (`rule_snapshot`), and the claim journal
 * is keyed by CUSTOMER rather than by claim. Same shape as `pr-lists`' `code@version`
 * — an identity that is genuinely a tuple travels as one.
 *
 * A row missing any of the three opens into nothing rather than into a guess.
 */
export function reviewRowKey(r: ReviewRow): string | null {
  if (r.id === null || r.customerId === null || r.claimId === null) return null
  return `${String(r.id)}:${String(r.customerId)}:${String(r.claimId)}`
}

export interface ParsedReviewKey {
  readonly reviewID: number
  readonly customerID: number
  readonly claimID: number
}

export function parseReviewKey(key: string): ParsedReviewKey | null {
  const parts = key.split(':')
  if (parts.length !== 3) return null
  const [a, b, c] = parts
  if (a === undefined || b === undefined || c === undefined) return null
  if (!/^\d+$/.test(a) || !/^\d+$/.test(b) || !/^\d+$/.test(c)) return null
  const reviewID = Number(a)
  const customerID = Number(b)
  const claimID = Number(c)
  if (![reviewID, customerID, claimID].every((n) => Number.isSafeInteger(n) && n > 0)) return null
  return { reviewID, customerID, claimID }
}

/**
 * Folds the queue response into the screen.
 *
 * Note what this does NOT do: a queue with zero rows still returns blocks, so
 * `liveView` never resolves to the shell's generic `empty` panel. That is
 * deliberate — the shell's empty copy comes from the corpus `Screen` spec and
 * cannot name `official` or explain why near-empty is the designed state, and
 * the difference between "the good state" and "a failed load" is the whole of
 * law 20 on this screen.
 */
export function composeReviewQueue(
  res: ReviewQueueResponse, caller: Caller, readAt: Date,
): ScreenData {
  const rows: ReviewRow[] = []
  let unreadable = 0
  for (const raw of res.reviews) {
    const parsed = readReview(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [queueTable(rows, unreadable)]
  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(QUEUE_EMPTY_NOTE)

  const missing: string[] = [
    'the depth of the whole queue (this endpoint sends no total, and no oldest-item age)',
    'customer names — the row carries an id and nothing else',
  ]
  if (caller.session === null) missing.push('what this session is permitted to do')
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  const noKey = rows.filter((r) => reviewRowKey(r) === null).length
  if (noKey > 0) {
    missing.push(`${String(noKey)} row(s) missing an id, a customer or a claim, which therefore open into nothing`)
  }

  const provenance: DataSource = {
    kind: 'partial',
    source: QUEUE_SOURCE,
    authority: AUTHORITY,
    readAt: readAtStamp(readAt),
    missing,
  }

  // The identity travels beside the drawing, never inside it: the first cell
  // renders `— no id` for a row whose id did not arrive, and a request built
  // from that string would decide whatever answered.
  return { blocks, provenance, rowKeys: rows.map(reviewRowKey) }
}

function isReviewQueueResponse(payload: unknown): payload is ReviewQueueResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<ReviewQueueResponse>).reviews)
}

/**
 * Reads the impersonation review queue, and who is reading it.
 *
 * Both in parallel, and the session read cannot fail the screen: `readCaller`
 * resolves either way, so a `/me` that refused leaves the queue readable with
 * the decision control disabled and the reason stated — which is exactly the
 * posture §6.18.1's Denied state asks for.
 *
 * No `limit` is sent. The endpoint's default is 50 and its cap is 200, and this
 * console has nothing to say about which: there is no cursor to page with, so a
 * higher limit would only move where the unreachable tail begins. A 400 on
 * `limit` is therefore a state this screen cannot reach.
 */
export const loadReviewQueue: ScreenLoader = async (api) => {
  const [payload, caller] = await Promise.all([
    api.get<unknown>(REVIEW_QUEUE_PATH).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return error
    }),
    readCaller(api),
  ])

  if (payload instanceof ApiError) {
    return queueUnread(whyUnread('The review queue endpoint', payload), payload.decisionRef)
  }
  if (payload instanceof Error) {
    return queueUnread(whyUnread('The review queue endpoint', payload), null)
  }
  if (!isReviewQueueResponse(payload)) {
    return queueUnread('The review queue endpoint returned a shape this console does not know: no reviews array was on it.', null)
  }
  return composeReviewQueue(payload, caller, new Date())
}

// ===========================================================================
// THE DECISION — POST /handles/reviews/{id}/decision
// ===========================================================================

/**
 * The reason codes this control offers, and where they came from.
 *
 * **These are this console's own list, not the managed registry.** There is no
 * reason-code registry read anywhere on the admin surface — `cf-reasons` is a
 * Phase-1 nav item with no endpoint behind it — so law 21's "reason codes come
 * from the managed registry" cannot be satisfied here. The honest response is to
 * offer a short closed list, say on screen that it is the console's own pending
 * the registry read, and never present it as the registry: an invented list
 * labelled as managed is the worse failure, because the next operator quotes it
 * as policy.
 *
 * They are split by outcome. An allowance recorded under `impersonation_likely`
 * is a contradiction that nobody can resolve six months later, and the audit row
 * is read by somebody who has only the code.
 */
export const DECISION_REASONS: readonly DecideReason[] = [
  ['not_impersonation', 'the fold matched, but this claimant is not passing as anybody', 'allow'],
  ['rights_holder', 'the claimant is the party the reserved term protects', 'allow'],
  ['term_overbroad', 'the reviewing term caught this name by accident', 'allow'],
  ['impersonation_likely', 'the name would read as the operator or a protected party', 'refuse'],
  ['no_right_established', 'the claimant did not establish a right to this name', 'refuse'],
  ['term_applies', 'the reviewing term applies to this name as published', 'refuse'],
]

/** The two outcomes, equally weighted, each with its consequence spelled out. */
const DECISION_OUTCOMES = [
  ['allow', 'Allow the name',
    'The handle goes live for this customer. If they already answered to another name, THIS decision is what puts it into quarantine — the claim did not; the response names the displaced handle and the instant it stops reaching them.',
    'n'],
  ['refuse', 'Refuse the name',
    'The name is retired PERMANENTLY. It is not returned to the pool, because the person most likely to ask for it next is the one just refused. The customer keeps whatever name they had.',
    'r'],
] as const

/** The wire shape of the 200. Only the first six fields are required. */
export interface DecisionResultView {
  readonly id: number
  readonly handle: string
  /** `allowed | refused` — a two-value closed set, not law 14's four. */
  readonly state: string
  readonly decided_at: string
  readonly decided_by: string
  readonly decision_reason: string
  /** Absent, not null: both are `omitempty` on the Go side. */
  readonly released_handle?: string
  readonly released_handle_resolves_until?: string
}

/**
 * The panel a recorded decision produces.
 *
 * Pure, so the interesting shapes — an allowance that displaced a name, one that
 * did not, a state the console does not know — are table-testable without a
 * client.
 */
export function composeDecisionPanel(payload: unknown, sent: string): DecidePanel {
  const raw = (typeof payload === 'object' && payload !== null && !Array.isArray(payload))
    ? payload as Partial<DecisionResultView>
    : {}

  const state = text(raw.state)
  const handle = text(raw.handle)
  const decidedBy = text(raw.decided_by)
  const decidedAt = stamp(text(raw.decided_at))
  const released = text(raw.released_handle)
  const until = stamp(text(raw.released_handle_resolves_until))

  const known = state === 'allowed' || state === 'refused'
  const heading = !known
    ? `The decision was recorded, and the backend reported a state this console does not know: ${state === '' ? '(none)' : state}`
    : state === 'allowed'
      ? `@${handle === '' ? '(name not returned)' : handle} is now live`
      : `@${handle === '' ? '(name not returned)' : handle} is retired, permanently`

  const lines: DecideLine[] = [
    ['State', state === '' ? '— not returned' : state,
      known ? '' : 'outside the two values this console knows (allowed, refused)'],
    ['Handle', handle === '' ? '— not returned' : `@${handle}`, ''],
    ['Decided by', decidedBy === '' ? '— not returned' : decidedBy,
      'the resolved staff session, never taken from a body'],
    ['Decided at', decidedAt === '' ? '— not returned' : decidedAt, 'UTC'],
    ['Reason recorded', text(raw.decision_reason) === '' ? sent : text(raw.decision_reason),
      'as it will read in the audit row'],
  ]

  if (released !== '') {
    // The two fields the whole "persistent panel, never a toast" rule exists
    // for: an address somebody is being paid to has just been put on a clock.
    lines.push(['Displaced handle', `@${released}`,
      'the name this customer was answering to until this allowance'])
    lines.push(['Stops reaching them', until === '' ? '— not returned' : until,
      'after this instant the name retires and belongs to nobody, for ever'])
  }

  const note = state === 'allowed'
    ? (released === ''
        ? 'Recorded once and for ever. This customer had no other name, so nothing was displaced. The row has left the queue: no endpoint in this backend returns a decided review, so there is no "recently decided" list, no undo, and nothing to re-read this as — the only lasting traces are the audit log and this customer\'s claim journal. The queue above was read before this decision and still shows the row; re-open the screen to see it gone.'
        : 'Recorded once and for ever, and a countdown has started on an address somebody is being paid to. Copy the two fields above now: no endpoint in this backend returns a decided review, so there is no "recently decided" list, no undo and nothing to re-read them from — the only lasting traces are the audit log and this customer\'s claim journal. The queue above was read before this decision and still shows the row; re-open the screen to see it gone.')
    : 'Recorded once and for ever. The name is retired and is not returned to the pool; the customer keeps whatever name they had, and nothing was displaced. The row has left the queue: no endpoint in this backend returns a decided review, so there is no undo and nothing to re-read this as — the only lasting traces are the audit log and this customer\'s claim journal. The queue above was read before this decision and still shows the row; re-open the screen to see it gone.'

  return {
    tone: !known ? 'r' : state === 'allowed' ? 'n' : 'a',
    heading,
    lines,
    note,
    final: true,
  }
}

/**
 * The panel when the decision did not happen.
 *
 * `final` is the judgement this function exists to make, and it is not the same
 * as "the request failed":
 *
 *   - A **404** is final. `no open impersonation review with that id` — that
 *     sentence and no more, which is all the server says and all it can say: an
 *     id that was decided and an id that never existed produce the same answer.
 *     The likelier reading is offered BESIDE the server's sentence and marked as
 *     this console's inference, never folded into it.
 *   - A **400** is final for this attempt in the sense that retrying the same
 *     body gets the same answer, but the operator can fix it, so the control
 *     stays alive.
 *   - A **403**, a **409 standby**, a **500** or an unreachable backend decided
 *     nothing. Killing the control after one of those would strand a creator
 *     whose name is held, so it stays live.
 */
export function composeDecisionFailure(error: unknown, sent: string): DecidePanel {
  if (!(error instanceof ApiError)) {
    return {
      tone: 'r',
      heading: 'The decision was not sent',
      lines: [['Reason sent', sent, 'nothing was recorded']],
      note: 'The backend could not be reached, so nothing was decided and the handle is still held. Nothing about this attempt reached the audit log. Try again.',
      final: false,
    }
  }

  const code = error.code === null ? '' : ` (${error.code})`
  const said = text(error.message)
  const lines: DecideLine[] = [
    ['Backend answered', `${String(error.status)}${code}`, ''],
    ['It said', said === '' ? '— no message' : said, 'the server’s own words, verbatim'],
    ['Reason sent', sent, ''],
  ]

  if (error.status === 404) {
    return {
      tone: 'a',
      heading: 'There is no open review with that id',
      lines,
      note: 'That sentence is the whole of what the backend said, and it cannot say more: an id that was already decided and an id that never existed are the same 404 on this route. This console\'s own inference — not the server\'s — is that somebody else has most likely decided it since this page was read. The audit log and the customer\'s claim journal are where the answer is; this queue is stale and should be re-opened.',
      final: true,
    }
  }
  if (error.status === 403) {
    const named = text(error.requiredRole)
    return {
      tone: 'a',
      heading: 'The backend refused this decision',
      lines,
      note: `Nothing was decided and the handle is still held. ${named === '' ? 'The refusal named no required role, so what this session is missing is not guessed at here.' : `The refusal states the required role as ${JSON.stringify(named)}.`} The console drew a control the server does not accept, which is the direction the server is meant to catch — enforcement is its, never this console's.`,
      final: false,
    }
  }
  if (error.status === 409) {
    return {
      tone: 'a',
      heading: 'This cell does not take writes',
      lines,
      note: 'Nothing was decided and the handle is still held. This is the standby cell; the decision happens on the primary. Nothing about this attempt reached the audit log.',
      final: false,
    }
  }
  if (error.status === 400) {
    return {
      tone: 'a',
      heading: 'The backend refused the decision as composed',
      lines,
      note: 'Nothing was decided and the handle is still held. The message above is the server\'s, verbatim — the reason is capped at 200 bytes after trimming and is refused rather than truncated, so no part of what was typed has been silently dropped. Accented and non-Latin letters cost more than one byte each, so a reason can exceed the cap while looking short. Adjust it and submit again.',
      final: false,
    }
  }
  return {
    tone: 'r',
    heading: 'The decision was not recorded',
    lines,
    note: 'Nothing was decided and the handle is still held. This is not a refusal and not a "no such review" — it is a failure, and the row is still somebody waiting. Try again, and raise it if it persists.',
    final: false,
  }
}

/**
 * The submit port for one review.
 *
 * Never rejects, per `DecideSubmit`: every answer the backend can give is a
 * panel the operator reads.
 *
 * No idempotency key. `RequestOptions.idempotencyKey` exists and is required on
 * every money-adjacent instruction (invariant 5) — and this is not one. No money
 * moves, no `decision_ref` is minted, and attaching a key would dress a
 * judgement about a NAME in the vocabulary of the screens that move money, which
 * is precisely what §6.18 forbids. The route's own protection against a double
 * submit is stronger than a key anyway: a second decision on the same review is
 * a 404, by construction.
 */
export function decisionSubmit(api: ApiClient, reviewID: number) {
  return async (outcome: string, reason: string): Promise<DecidePanel> => {
    // `allow` is derived from an explicit outcome value and never defaulted. An
    // outcome this function does not recognise sends nothing at all rather than
    // falling through to `false`, which is the value that retires a name.
    if (outcome !== 'allow' && outcome !== 'refuse') {
      return {
        tone: 'r',
        heading: 'Nothing was sent',
        lines: [['Outcome received', outcome === '' ? '— none' : outcome, 'not one of allow, refuse']],
        note: 'This console did not recognise the outcome, so it sent nothing. A missing or unreadable allow would be read as false by the backend, and false retires the name for ever — so it is refused here rather than guessed at.',
        final: false,
      }
    }
    try {
      const payload = await api.post<unknown>(reviewDecisionPath(reviewID), {
        allow: outcome === 'allow',
        reason,
      })
      return composeDecisionPanel(payload, reason)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return composeDecisionFailure(error, reason)
    }
  }
}

/**
 * The decision control for one held handle.
 *
 * `submit` is attached only when this session may actually decide. A control
 * with a live port behind a disabled button is one keystroke of a future
 * refactor away from being clickable, and the safe shape is that the port is
 * absent when the permission is.
 */
export function decideBlock(
  key: ParsedReviewKey, handle: string, caller: Caller, api: ApiClient | null,
): DecideBlock {
  const refusal = decideRefusal(caller)
  const named = handle === '' ? `review ${String(key.reviewID)}` : `@${handle}`
  return {
    t: 'decide',
    title: `Decide ${named}`,
    lead: `Allow or refuse ${named} — one decision, made once, by you`,
    body: `This is a judgement about a NAME and nothing else: no money moves, no limit changes, and no decision_ref is minted — a handle authorises nothing. The name is held right now, which means it is taken against everybody else and resolves to nobody, so the customer waiting on it has a pay-me link that does not work. Read the claim above before choosing: the rule snapshot on it is the full ruleset consulted, and the queue's one-line reason names only the first term that matched. Neither outcome can be taken back, and there is no endpoint anywhere that returns a decided review.`,
    outcomes: DECISION_OUTCOMES.map(([value, label, consequence, tone]) =>
      [value, label, consequence, tone] as const),
    reasons: DECISION_REASONS,
    reasonNote: `This console's own list, pending the reason-code registry — cf-reasons has no endpoint on the admin surface, so law 21's managed registry cannot be read. The code and the note are joined as "code: note" into the one free-text reason field this route has, capped at ${String(REASON_MAX_LENGTH)} bytes — not characters, because the server's cap is a byte count, so accented and non-Latin letters cost more than one each — and refused rather than truncated.`,
    auditLine: `will be recorded: actor · admin.handle.impersonation.decide · review ${String(key.reviewID)} · reason`,
    cta: 'Record this decision',
    ctaNote: 'Recorded once and for ever. Refusing retires the name permanently; allowing makes it live and starts the quarantine clock on whatever name this customer answered to before.',
    blocked: refusal,
    ...(refusal === '' && api !== null
      ? { submit: decisionSubmit(api, key.reviewID) }
      : {}),
  }
}

